<script setup lang="ts">
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import TextFieldInput from "@/components/Reusables/Inputs/TextFieldInput.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import BimCodeSelect from "@/components/Views/MaterialMapping/CreateSpeckleGroup/BimCodeSelect.vue";
import { computed } from "vue";

import type { TooltipBindingValue } from "@/directives/tooltipDirective";

const props = defineProps<{
  material: any;
  canEdit: boolean;
  // The hint shown on the controls this section grays out. Handed down rather than resolved here so
  // one composable in the host decides both *whether* the section is editable and *why* it is not,
  // and the two can never drift apart.
  readOnlyTooltip: TooltipBindingValue;
  selectedMaterialLabel: string | undefined;
  lifeTimeErrors: Array<string>;
  lifeTimeOverwrite?: number;
  materialMappingIsType: boolean;
  isSaving: boolean;
  lifeTimeUpdaterKey?: string | number | boolean;
  speckleClassificationParam?: string;
}>();

const ccsClassification = computed(() => {
  const param = props.speckleClassificationParam;
  if (param && props.material?.additionalParameters?.[param]) {
    return props.material.additionalParameters[param];
  }
  return props.material?.additionalParameters?.CCSClassification;
});

const emits = defineEmits<{
  toggleLifetimeTable: [];
  setBimCode: [payload: string];
  onCcsClassification: [payload: { CCSClassification: string; classificationObjectClass: string }];
}>();

const lifeTimeEditDisabled = computed(() => {
  return !props.canEdit || props.lifeTimeOverwrite !== undefined;
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Lifetime group: keep the lifetime-related fields on one row -->
    <div class="flex flex-wrap items-start gap-4">
      <!-- Lifetime -->
      <div
        v-if="!material.mapping?.isConstruction"
        class="flex flex-col gap-1">
        <span class="t-caption text-type-muted">Lifetime (years)</span>
        <div class="flex flex-wrap items-center gap-2">
          <div class="w-32">
            <NumberInput
              v-if="lifeTimeOverwrite"
              :value="lifeTimeOverwrite"
              color="primary"
              :disabled="lifeTimeEditDisabled"
              :no-decimals="true"
              :positive="true"
              placeholder="Custom lifetime"
              :max="300" />
            <NumberInput
              v-else
              v-model:value="material.lifeTime"
              color="primary"
              :disabled="lifeTimeEditDisabled"
              :no-decimals="true"
              :positive="true"
              placeholder="Custom lifetime"
              :max="300"
              :error-msg="lifeTimeErrors.length > 0 && !material.lifeTime && !isSaving ? 'Set lifetime' : undefined" />
          </div>

          <!-- Stays rendered and explains itself when editing is not allowed, rather than vanishing
               (RTLCA-5585). `aria-disabled` rather than the native attribute: Chrome fires no mouse
               events on a natively disabled button, so the tooltip saying why would never appear.
               The host's `toggleLifetimeTable` handler refuses the click regardless. -->
          <button
            v-if="!material.autoMapLocal"
            v-tooltip="readOnlyTooltip"
            type="button"
            :aria-disabled="!canEdit || undefined"
            class="focus-ring interactive-active t-small text-type-default duration-fast ease-standard rounded-8 bg-slate-0 shrink-0 border border-slate-200 px-3 py-1.5 transition-colors hover:bg-slate-100"
            :class="{ 'interactive-disabled': !canEdit }"
            @click="() => emits('toggleLifetimeTable')">
            Lifetime table
          </button>
        </div>
      </div>

      <!-- Derived from EPD -->
      <div
        v-if="!material.mapping?.isConstruction && !material.autoMapLocal && material.mapping?.expectedLifespan"
        class="flex flex-col gap-1">
        <span class="t-caption text-type-muted">Derived from EPD (years)</span>
        <div class="w-32">
          <NumberInput
            :value="material.mapping?.expectedLifespan ?? null"
            color="primary"
            :disabled="!canEdit || (material.mapping?.expectedLifespan !== null && material.mapping?.expectedLifespan !== 0)"
            no-decimals
            positive
            placeholder="EPD lifetime"
            :max="300"
            :error-msg="lifeTimeErrors.length > 0 && !material.lifeTime && !isSaving ? 'Set lifetime' : undefined"
            @update:value="
              (val: number | null) => {
                if (material?.mapping) material.mapping.expectedLifespan = val;
              }
            " />
        </div>
      </div>

      <!-- Lifetime offset -->
      <div class="flex flex-col gap-1">
        <span class="t-caption text-type-muted">Lifetime offset</span>
        <div class="w-32">
          <NumberInput
            v-if="!material.autoMapLocal"
            v-model:value="material.lifeTimeOffset"
            color="primary"
            :disabled="!canEdit || (!material.lifeTime && !material.mapping?.expectedLifespan)"
            :no-decimals="true"
            :positive="true"
            placeholder="New lifetime offset"
            :max="300" />
        </div>
      </div>
    </div>

    <!-- Classification / BIM code -->
    <div
      v-if="!materialMappingIsType"
      class="flex flex-col gap-1">
      <span class="t-caption text-type-muted">Classification</span>
      <!-- The text-ish fields fill the column (RTLCA-5181). The numeric lifetime inputs above keep their
           w-32: a years box sized to the column would read as a mistake. -->
      <div class="w-full">
        <BimCodeSelect
          :disabled="!canEdit"
          :bim-type-code="material?.additionalParameters?.BIMTypeCode"
          :ccs-classification="ccsClassification"
          :classification-object-class="material?.additionalParameters?.classificationObjectClass"
          :required="false"
          @value-change="(val: string) => emits('setBimCode', val)"
          @on-c-c-s-classification="(payload: any) => emits('onCcsClassification', payload)" />
      </div>
    </div>

    <!-- Name -->
    <div class="flex flex-col gap-1">
      <span class="t-caption text-type-muted">{{ `${selectedMaterialLabel} name` }}</span>
      <div class="w-full">
        <TextInputNew
          v-if="!material.autoMapLocal"
          v-model="material.identifier"
          :disabled="!canEdit"
          :placeholder="`${selectedMaterialLabel} name`" />
      </div>
    </div>

    <!-- Description -->
    <div class="flex flex-col gap-1">
      <span class="t-caption text-type-muted">Description</span>
      <div class="w-full">
        <TextFieldInput
          v-if="!material.autoMapLocal"
          v-model:value="material.description"
          size="w-full h-16"
          :disabled="!canEdit"
          color="primary"
          class="font-normal"
          placeholder="Description" />
      </div>
    </div>
  </div>
</template>
